import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { GlobalStateService } from '@app/shared/states';
import { map } from 'rxjs/operators';
import { TokenDetails } from '@app/core/models/token-detail.model';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private http: HttpClient,
    private auth0Service: Auth0Service,
    private globalState: GlobalStateService
  ) {}

  /**
   * Get all users list
   */
  getAllUsers(params) {
    return this.http.get<TokenDetails>(`/users`, { params: params });
  }

  /**
   * Get database connections
   */
  getDBConnection(params) {
    return this.http.get<TokenDetails>(`/connections`, { params: params });
  }
  /**
   * Get users role by userid
   */
  getUsersRole(userId) {
    return this.http.get<TokenDetails>(`/users/${userId}/roles`);
  }

  /**
   * Get user permissions
   */
  getUserIdPermissions(userId) {
    return this.http.get<TokenDetails>(`/users/${userId}/permissions`);
  }

  /**
   * Get all the resources
   */
  getPermissionResouce() {
    return this.http.get<TokenDetails>(`/resource-servers`);
  }

  /**
   * Get user details from user id
   */
  getUserProfile(userId) {
    return this.http.get<TokenDetails>(`/users/${userId}`);
  }

  /**
   * Get all the user by email-id
   */
  getUserByEmailAddress(email) {
    return this.http.get<TokenDetails>(`/users-by-email`, { params: email });
  }

  /**
   * Get all the user by email-id
   */
  getGuardianFactorApi() {
    return this.http.get<TokenDetails>(`/guardian/factors`);
  }
  /**
   * Get all logs
   */
  getLogsListApi(params) {
    return this.http.get<TokenDetails>(`/logs`, { params: params });
  }

  /**
   * Get all logs
   */
  getAuthPolicyApi() {
    return this.http.get<TokenDetails>(`/guardian/policies`);
  }
  /**
   * Update user metadata
   */
  updateUser(userId, body) {
    return this.http.patch<TokenDetails>(`/users/${userId}`, body).pipe(
      map((data) => {
        return data;
      })
    );
  }
  /**
   * Update user metadata
   */
  updateGuardian(type, body) {
    return this.http.put<TokenDetails>(`/guardian/factors/${type}`, body).pipe(
      map((data) => {
        return data;
      })
    );
  }
  /**
   * Update user metadata
   */
  updateAuthPolicies(body) {
    return this.http.put<TokenDetails>(`/guardian/policies`, body).pipe(
      map((data) => {
        return data;
      })
    );
  }
  /**
   * Delete user
   */
  deleteUserApi(userId) {
    return this.http.delete<TokenDetails>(`/users/${userId}`);
  }

  /**
   * Get all the roles
   */
  getRoles() {
    return this.http.get<TokenDetails>(`/roles`);
  }

  /**
   * Get all the applications
   */
  getAllApplicationApi(params) {
    return this.http.get<TokenDetails>(`/clients`, { params: params });
  }

  /**
   * Assign role to user
   */
  assignRole(userId, body) {
    return this.http.post<TokenDetails>(`/users/${userId}/roles`, body).pipe(
      map((data) => {
        return data;
      })
    );
  }

  /**
   * Create new user
   */
  createUser(body) {
    return this.http.post<TokenDetails>(`/users`, body).pipe(
      map((data) => {
        return data;
      })
    );
  }

  /**
   * Remove role of user
   */
  removeRole(userId, body) {
    return this.http.delete<TokenDetails>(`/users/${userId}/roles`, { body: body }).pipe(
      map((data) => {
        return data;
      })
    );
  }

  /**
   * Assign permissions to user
   */
  assignPermission(userId, body) {
    return this.http.post<TokenDetails>(`/users/${userId}/permissions`, body).pipe(
      map((data) => {
        return data;
      })
    );
  }

  /**
   * Remove user permission
   */
  removePermission(userId, body) {
    return this.http.delete<TokenDetails>(`/users/${userId}/permissions`, { body: body }).pipe(
      map((data) => {
        return data;
      })
    );
  }
  /**
   * Link user account with other identities
   */
  linkUserAccount(userId, body) {
    return this.http.post<TokenDetails>(`/users/${userId}/identities`, body).pipe(
      map((data) => {
        return data;
      })
    );
  }

  /**
   * Get all the organizations
   */
  getOrganizationsApi() {
    return this.http.get(`/organizations`);
  }

  /**
   * Unlink user account
   */
  unLinkUserAccount(user) {
    return this.http
      .delete<TokenDetails>(
        `/users/${user.primaruUserId}/identities/${user.provider}/${user.secondaryUserId}`
      )
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  /**
   * Set authentication to state
   */
  updateUserLoginState() {
    let isAuthenticated$ = this.auth0Service.isAuthenticated$;
    isAuthenticated$.subscribe((data) => {
      this.globalState.setUserAuthentication(data);
    });
  }

  /* API call POST method to reset password */
  getAuthTokenApi(body) {
    return this.http.post<TokenDetails>('oauth/token/', body).pipe(
      map((data) => {
        return data;
      })
    );
  }

  /**
   * Get user details from auth0 user
   */
  getUserDetails() {
    return new Promise((resolve) => {
      this.auth0Service.user$.subscribe((data: any) => {
        if (data) {
          resolve(data);
        }
      });
    });
  }

  sendResetPasswordLink(body) {
    return this.http.post<TokenDetails>('/dbconnections/change_password', body).pipe(
      map((data) => {
        return data;
      })
    );
  }
  /**
   * Export Users data in csv
   */
  exportUsersList(body) {
    return this.http.post<TokenDetails>('/jobs/users-exports', body).pipe(
      map((data) => {
        return data;
      })
    );
  }
  /**
   * Get all logs
   */
  checkExportJobStatus(jobId) {
    return this.http.get<TokenDetails>(`/jobs/${jobId}`);
  }
}
